/**
 * Pure calendar-grid math for DatePickerPopover (first-run-setup.md SS5.3).
 * No React, no I/O — plain date arithmetic so it's trivially unit-testable
 * and reusable by the Monday-first grid + keyboard navigation.
 *
 * All dates are plain 'YYYY-MM-DD' strings (IsoDate's runtime shape) to
 * avoid timezone drift from the JS Date object's local-vs-UTC ambiguity —
 * every computation here works in whole days against UTC midnight.
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export interface CalendarCell {
  readonly date: string;
  readonly day: number;
  readonly inCurrentMonth: boolean;
}

export const WEEKDAY_LABELS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export function parseIsoDate(date: string): { year: number; month: number; day: number } {
  const [y, m, d] = date.split('-').map(Number);
  return { year: y ?? 1970, month: (m ?? 1) - 1, day: d ?? 1 };
}

export function toIsoDate(year: number, month: number, day: number): string {
  const d = new Date(Date.UTC(year, month, day));
  const yy = d.getUTCFullYear().toString().padStart(4, '0');
  const mm = (d.getUTCMonth() + 1).toString().padStart(2, '0');
  const dd = d.getUTCDate().toString().padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

export function addDays(date: string, days: number): string {
  const { year, month, day } = parseIsoDate(date);
  const ms = Date.UTC(year, month, day) + days * MS_PER_DAY;
  const d = new Date(ms);
  return toIsoDate(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/** Mon=0 .. Sun=6, matching the product's standing Mon-Sun week convention. */
function mondayFirstWeekday(date: string): number {
  const { year, month, day } = parseIsoDate(date);
  const jsDay = new Date(Date.UTC(year, month, day)).getUTCDay(); // Sun=0..Sat=6
  return (jsDay + 6) % 7;
}

export function monthLabel(year: number, month: number): string {
  return `${MONTH_NAMES[month]} ${year}`;
}

export function stepMonth(year: number, month: number, delta: number): { year: number; month: number } {
  const total = year * 12 + month + delta;
  return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 };
}

/**
 * Full Monday-first grid for one month, including leading/trailing filler
 * days from the adjacent months so every week row has exactly 7 cells.
 */
export function buildMonthGrid(year: number, month: number): readonly CalendarCell[] {
  const firstOfMonth = toIsoDate(year, month, 1);
  const leadingCount = mondayFirstWeekday(firstOfMonth);
  const gridStart = addDays(firstOfMonth, -leadingCount);

  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const totalCells = Math.ceil((leadingCount + daysInMonth) / 7) * 7;

  const cells: CalendarCell[] = [];
  for (let i = 0; i < totalCells; i += 1) {
    const cellDate = addDays(gridStart, i);
    const { month: cellMonth, day } = parseIsoDate(cellDate);
    cells.push({ date: cellDate, day, inCurrentMonth: cellMonth === month });
  }
  return cells;
}

export function formatLongDate(date: string): string {
  const { year, month, day } = parseIsoDate(date);
  return `${MONTH_NAMES[month]} ${day}, ${year}`;
}

/** "Aug 1, 2026" — the closed-field format (first-run-setup.md SS5.2). */
export function formatShortFieldDate(date: string): string {
  const { year, month, day } = parseIsoDate(date);
  return `${(MONTH_NAMES[month] ?? '').slice(0, 3)} ${day}, ${year}`;
}

export function isSameDate(a: string, b: string): boolean {
  return a === b;
}

export function compareDates(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}
